/**
 * Axios observability: add x-app-name, x-device-id, traceparent; timing; report to Faro on 5xx/network/timeout.
 * No duplicate toasts; no sensitive data in logs.
 */

import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { OBSERVABILITY_ENABLED, HEADER_APP_NAME, HEADER_DEVICE_ID, HEADER_TRACEPARENT } from './constants';
import { getDeviceId } from './device.service';
import { getSessionId } from './session.service';
import { getTraceparentHeader } from './tracing.service';
import { getAppName } from './faro.config';
import { pushFaroEvent } from './faro.config';
import type { ApiErrorType } from './types';

const OBSERVABILITY_START_KEY = '_observabilityStart';

function normalizeApiError(error: AxiosError): ApiErrorType {
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || (error.message && /timeout/i.test(error.message))) {
      return 'timeout';
    }
    return 'network';
  }
  const status = error.response.status;
  if (status >= 500) return '5xx';
  if (status >= 400) return '4xx';
  return 'unknown';
}

function reportApiErrorToFaro(
  error: AxiosError,
  config: InternalAxiosRequestConfig & { [key: string]: unknown }
): void {
  if (!OBSERVABILITY_ENABLED) return;
  try {
    const start = config?.[OBSERVABILITY_START_KEY] as number | undefined;
    const durationMs = typeof start === 'number' ? Math.round(performance.now() - start) : undefined;
    const url = config?.url ?? config?.baseURL ?? 'unknown';
    const method = (config?.method ?? 'get').toUpperCase();
    const errorType = normalizeApiError(error);
    const status = error.response?.status;

    pushFaroEvent('api_error', {
      error_type: errorType,
      url: typeof url === 'string' ? url : String(url),
      method,
      status: status ?? undefined,
      duration_ms: durationMs,
      device_id: getDeviceId(),
      session_id: getSessionId(),
      app_name: getAppName(),
    });
  } catch {
    // avoid breaking the app if Faro fails
  }
}

export function addObservabilityInterceptors(instance: AxiosInstance): void {
  if (!OBSERVABILITY_ENABLED) return;

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)[HEADER_APP_NAME] = getAppName();
      (config.headers as Record<string, string>)[HEADER_DEVICE_ID] = getDeviceId();
      const traceparent = getTraceparentHeader();
      if (traceparent) (config.headers as Record<string, string>)[HEADER_TRACEPARENT] = traceparent;
      (config as unknown as Record<string, number>)[OBSERVABILITY_START_KEY] = performance.now();
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const config = error.config as (InternalAxiosRequestConfig & { [key: string]: unknown }) | undefined;
      if (config) {
        const errorType = normalizeApiError(error);
        if (errorType === 'network' || errorType === 'timeout' || errorType === '5xx') {
          reportApiErrorToFaro(error, config);
        }
      }
      return Promise.reject(error);
    }
  );
}
